import React from 'react'
import DOMPurify from 'dompurify'
import { Icon, Button, IconButton } from '../../../atoms'
import { NoteEditorForm } from '../NoteEditorForm'
import { formatTimestamp, highlightText } from '../utils'
import {
  NoteItem,
  NoteHeader,
  NoteMeta,
  NoteAuthor,
  NoteSeparator,
  NoteTimestamp,
  NoteActions,
  NoteContent,
  DeleteConfirm,
  DeleteText,
  DeleteActions,
} from './NoteCard.styles'
import type { NoteCardProps } from './NoteCard.types'

const defaultTranslations = {
  confirmDelete: 'Are you sure you want to delete this note?',
  cancel: 'Cancel',
  delete: 'Delete',
  editNote: 'Edit note',
  deleteNote: 'Delete note',
  saveNote: 'Save Note',
}

export const NoteCard: React.FC<NoteCardProps> = ({
  note,
  searchQuery = '',
  isEditing = false,
  editContent = '',
  onEditContentChange,
  onEdit,
  onSave,
  onCancelEdit,
  onDelete,
  isDeleting = false,
  onConfirmDelete,
  onCancelDelete,
  translations,
}) => {
  const t = { ...defaultTranslations, ...translations }
  const sanitizeHtml = (html: string) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        'a',
        'ul',
        'ol',
        'li',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'code',
        'pre',
        'mark',
        'span',
        'div',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    })
  }

  return (
    <NoteItem>
      {isDeleting && (
        <DeleteConfirm>
          <DeleteText>{t.confirmDelete}</DeleteText>
          <DeleteActions>
            <Button variant="secondary" size="small" onClick={onCancelDelete}>
              {t.cancel}
            </Button>
            <Button variant="danger" size="small" onClick={onConfirmDelete}>
              {t.delete}
            </Button>
          </DeleteActions>
        </DeleteConfirm>
      )}

      <NoteHeader>
        <NoteMeta>
          <NoteAuthor>{note.userName || note.authorName}</NoteAuthor>
          <NoteSeparator>•</NoteSeparator>
          <NoteTimestamp>
            {formatTimestamp(note.createdAt, translations?.time)}
          </NoteTimestamp>
        </NoteMeta>
        {(onEdit || onDelete) && (
          <NoteActions>
            {onEdit && (
              <IconButton
                size="small"
                variant="default"
                onClick={onEdit}
                title={t.editNote}
                aria-label={t.editNote}
              >
                <Icon name="edit" size="small" />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                size="small"
                variant="default"
                aria-label={t.deleteNote}
                onClick={onDelete}
                title={t.deleteNote}
              >
                <Icon name="delete" size="small" />
              </IconButton>
            )}
          </NoteActions>
        )}
      </NoteHeader>

      {isEditing ? (
        <NoteEditorForm
          content={editContent}
          onChange={onEditContentChange}
          onSave={onSave}
          onCancel={onCancelEdit}
          saveLabel={t.saveNote}
        />
      ) : (
        <NoteContent>
          {searchQuery ? (
            <div
              dangerouslySetInnerHTML={{
                __html: highlightText(note.content, searchQuery),
              }}
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(note.content),
              }}
            />
          )}
        </NoteContent>
      )}
    </NoteItem>
  )
}
